import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Star, MessageSquare } from "lucide-react";
import { Button } from "./Button";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { createReview } from "../../redux/Slices/reviewSlice";
import { CreateReviewDto, ReviewStatusEnum } from "../../modules/shared/model";
import { GlobalService } from "../../modules";
import StarRating from "./StarRating";

interface ProfessionalReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestoneId: string;
  clientId: string;
  contractId: string;
  onReviewSubmitted?: () => void;
  milestone: any;
}

const ProfessionalReviewModal: React.FC<ProfessionalReviewModalProps> = ({
  isOpen,
  onClose,
  milestoneId,
  clientId,
  contractId,
  milestone,
  onReviewSubmitted
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector((state) => state.reviews);

  const [ratings, setRatings] = useState({
    clarity: 0,
    communication: 0,
    reliability: 0,
    payment: 0,
    availability: 0,
    overallExperience: 0
  });

  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ratingCriteria = [
    {
      key: "clarity" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.clarity.title"),
      description: t("professionalReviewModal.criteria.clarity.description"),
      required: true
    },
    {
      key: "communication" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.communication.title"),
      description: t("professionalReviewModal.criteria.communication.description"),
      required: true
    },
    {
      key: "reliability" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.reliability.title"),
      description: t("professionalReviewModal.criteria.reliability.description"),
      required: true
    },
    {
      key: "payment" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.payment.title"),
      description: t("professionalReviewModal.criteria.payment.description"),
      required: true
    },
    {
      key: "availability" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.availability.title"),
      description: t("professionalReviewModal.criteria.availability.description"),
      required: true
    },
    {
      key: "overallExperience" as keyof typeof ratings,
      title: t("professionalReviewModal.criteria.overallExperience.title"),
      description: t("professionalReviewModal.criteria.overallExperience.description"),
      required: true
    }
  ];

  const handleRatingChange = (key: keyof typeof ratings, rating: number) => {
    setRatings(prev => ({
      ...prev,
      [key]: rating
    }));
  };

  const isFormValid = () => {
    return Object.values(ratings).every(rating => rating > 0);
  };

  const calculateOverallRating = () => {
    const values = Object.values(ratings);
    return parseFloat((values.reduce((sum, rating) => sum + rating, 0) / values.length).toFixed(1));
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      GlobalService.showToast("error", t("toast.titles.error"), t("professionalReviewModal.rateAllCriteria"));
      return;
    }

    if (!user?.id) {
      GlobalService.showToast("error", t("toast.titles.error"), t("professionalReviewModal.userNotFound"));
      return;
    }

    setIsSubmitting(true);
    console.log(milestone, "milestone");
    try {
      const reviewData: CreateReviewDto = {
        milestone: { id: milestoneId },
        author: { id: user.id },
        user: { id: clientId },
        status: { id: ReviewStatusEnum.Active },
        rating: calculateOverallRating(),
        text: comment.trim() || null,
        clarity: ratings.clarity,
        communication: ratings.communication,
        reliability: ratings.reliability,
        payment: ratings.payment,
        availability: ratings.availability,
        overallExperience: ratings.overallExperience,
        helpful: 0,
        notHelpful: 0,
        shares: 0,
        views: 0,
        reply: "",
        canReply: milestone.reviews.length > 0 ? true : false
      };

      await dispatch(createReview(reviewData)).unwrap();

      GlobalService.showToast("success", t("toast.titles.success"), t("professionalReviewModal.reviewSubmittedSuccess"));

      // Reset form
      setRatings({
        clarity: 0,
        communication: 0,
        reliability: 0,
        payment: 0,
        availability: 0,
        overallExperience: 0
      });
      setComment("");

      onReviewSubmitted?.();
      onClose();
    } catch (error: any) {
      console.error("Failed to submit review:", error);
      GlobalService.showToast("error", t("toast.titles.error"), error?.message || t("professionalReviewModal.reviewSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-[10003] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t("professionalReviewModal.title")}</h2>
              <p className="text-gray-600 mt-1">{t("professionalReviewModal.subtitle")}</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {ratingCriteria.map((criterion, index) => (
              <div key={criterion.key} className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {index + 1}. {criterion.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {criterion.description}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StarRating
                    rating={ratings[criterion.key]}
                    onRatingChange={(rating) => handleRatingChange(criterion.key, rating)}
                    disabled={isSubmitting}
                    size="lg"
                  />
                  <span className="text-sm text-gray-500">
                    {ratings[criterion.key] > 0 ? `${ratings[criterion.key]} ${ratings[criterion.key] !== 1 ? t("professionalReviewModal.starsPlural") : t("professionalReviewModal.stars")}` : t("professionalReviewModal.notRated")}
                  </span>
                </div>
              </div>
            ))}

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t("professionalReviewModal.additionalComments")}</h3>
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t("professionalReviewModal.additionalCommentsPlaceholder")}
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={isSubmitting}
              />
              <p className="text-xs text-gray-500">
                {t("professionalReviewModal.additionalCommentsHelper")}
              </p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900">{t("professionalReviewModal.overallRating")}</span>
              </div>
              <div className="flex items-center gap-3">
                <StarRating
                  rating={calculateOverallRating()}
                  onRatingChange={() => { }} // Read-only
                  disabled={true}
                  size="lg"
                />
                <span className="text-sm text-blue-700">
                  {calculateOverallRating() > 0 ? `${calculateOverallRating()}/5` : t("professionalReviewModal.completeAllRatings")}
                </span>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-3">
            <Button
              onClick={handleClose}
              variant="outline"
              disabled={isSubmitting}
            >
              {t("professionalReviewModal.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? t("professionalReviewModal.submitting") : t("professionalReviewModal.submitReview")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfessionalReviewModal;
